import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Row,
  Grid,
  Col,
  Input,
  Dropdown,
  Tooltip,
  Menu,
  Checkbox,
  message,
  Drawer,
  Tag
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { App } from "antd";
import { fetchAccountsList, deleteAccount } from "../accountsThunks";
import { setSelectedAccount, setIsEditAccount } from "../accountsSlice";
import { selectAccountsList } from "../accountsSelectors";
import commaNumber from "comma-number";
import dayjs from "dayjs";
import Papa from "papaparse";
import { formatDateOnly, getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";
import Swal from "sweetalert2";

const format = commaNumber.bindWith(",", ".");

// ALL COLUMNS AVAILABLE
const ALL_COLUMNS = [
  { key: "acc_code", label: "Code" },
  { key: "acc_name", label: "Account Name" },
  { key: "acc_type_name", label: "Account Type" },
  { key: "opening_balance", label: "Opening Balance" },
  { key: "created_date", label: "Entry Date" },
];

const DEFAULT_VISIBLE = [
  "acc_code",
  "acc_name",
  "acc_type_name",
  "opening_balance",
  "created_date",
];

const AccountList = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
 
  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("account_entry");
    access_type_set(access);
  }, []);

  const rawAccountsList = useSelector(selectAccountsList);

  const isAdminUser = useIsAdminUser();
   const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  // CGST/SGST/IGST are an India-only concept; hide them outright for
  // non-India companies, and for India only when GST is disabled.
  const gstOnlyAccountNames = ["CGST SGST IGST Credit", "CGST SGST IGST Payable"];
  // The generic tax account (shown as "GST Account" for India) should stay
  // visible for non-India companies (VAT still applies), and hide only when
  // India has GST disabled.
  const indiaTaxAccountNames = ["GST Account", "Vat & Tax Account"];
  const accountsList = useMemo(
    () =>
      rawAccountsList.filter((acc) => {
        if (gstOnlyAccountNames.includes(acc.acc_name)) {
          return isIndia && gstEnabled;
        }
        if (indiaTaxAccountNames.includes(acc.acc_name)) {
          return !isIndia || gstEnabled;
        }
        return true;
      }),
    [rawAccountsList, isIndia, gstEnabled]
  );

  const defaults = [
    "Discount on sale",
    "Discount on service",
    "Transport Cost on Purchase",
    "Sales",
    "Sales Return",
    "Discount on purchase",
    "Transport Cost on Sales",
    "Vat & Tax Account",
    "Purchase",
    "Purchase Return",
    "Salary",
    "Cash",
    "Capital Account",
    "Services Account",
    "Service Expense Account",
    "PDC",
    "CDC",
    "Discount on service",
    "Services",
    "Service Expense",
    "Investment in Shares",
    "Investment in Bonds",
    "Investment in Property/Plot etc.",
    "Long term investment",
    "Short term investment",
    "Prepaid Maintenance Expense",
    "Prepaid Expense",
    "Prepaid Rent",
    "Prepaid Insurance Charges",
    "Interest Receivables",
    "Bill Receivable",
    "Accrued Income",
    "Mutual Fund",
    "CGST SGST IGST Credit",
    "Bill Drawn",
    "Bill Payable",
    "CGST SGST IGST Payable",
    "Security Deposit",
    "Electricity Deposit",
    "Rent Deposit",
  ];

  // SEARCH TEXT
  const [searchText, setSearchText] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  // VISIBLE COLUMNS STATE
  const [visibleKeys, setVisibleKeys] = useState(DEFAULT_VISIBLE);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // FILTERED DATA
  const filteredAccounts = useMemo(() => {
    if (!searchText.trim()) return accountsList;
    return accountsList.filter((it) =>
      Object.values(it || {})
        .join(" ")
        .toLowerCase()
        .includes(searchText?.toLowerCase()),
    );
  }, [accountsList, searchText]);
// Build tree structure from flat list
const treeAccounts = useMemo(() => {
  if (!searchText.trim()) {
    const map = {};
    const roots = [];

    accountsList.forEach(acc => {
      map[acc.acc_id] = { ...acc, children: [] };
    });

    accountsList.forEach(acc => {
      if (acc.parent_acc_id && map[acc.parent_acc_id]) {
        map[acc.parent_acc_id].children.push(map[acc.acc_id]);
      } else {
        roots.push(map[acc.acc_id]);
      }
    });

    // ✅ Recursive — cleans ALL levels not just root
    const clean = (nodes) =>
      nodes.map(n => ({
        ...n,
        children: n.children.length ? clean(n.children) : undefined,
      }));

    return clean(roots);
  }

  return accountsList.filter(it =>
    Object.values(it || {})
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );
}, [accountsList, searchText]);
  // DELETE HANDLER
  // const handleDelete = async (acc_id, acc_name) => {
  //   try {
  //     await dispatch(deleteAccount(acc_id)).unwrap();
  //     modal.success({
  //       title: `Account "${acc_name}" deleted successfully`,
  //     });
  //     dispatch(fetchAccountsList());
  //   } catch (error) {
  //     modal.warning({
  //       title: error.message || "Delete failed",
  //     });
  //   }
  // };

  // const handleDelete = async (acc_id, acc_name) => {
  //   try {
  //     const res = await dispatch(deleteAccount(acc_id)).unwrap();
  //     console.log("res", res.msg);

  //     modal.success({
  //       title:
  //         `${acc_name} ${res?.msg} ` ||
  //         `Account "${acc_name}" deleted successfully`,
  //     });

  //     dispatch(fetchAccountsList());
  //   } catch (error) {
  //     modal.warning({
  //       title: error?.message || "Delete failed",
  //     });
  //   }
  // };
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDelete = async (acc_id, acc_name) => {
  const result = await Swal.fire({
    title: "Are you sure?",
    text: `Do you want to delete "${acc_name}"?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
  });

    if (!result.isConfirmed) return;

    try {
      const res = await dispatch(deleteAccount(acc_id)).unwrap();

      Swal.fire({
        title: res?.msg
          ? `${acc_name} ${res.msg}`
          : `Account "${acc_name}" deleted successfully`,
        icon: "success",
      });

      dispatch(fetchAccountsList());
    } catch (error) {
      Swal.fire({
        title: error?.message || "Delete failed",
        icon: "warning",
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAccountsList());
  }, [dispatch]);

  // EXPORT CSV
  const exportCSV = () => {
    try {
      const csv = Papa.unparse(filteredAccounts);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", `accounts-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      message.error("Export failed");
    }
  };

  // COLUMN DROPDOWN UI
  const columnMenu = (
    <Menu>
      {ALL_COLUMNS.map((col) => (
        <Menu.Item key={col.key}>
          <Checkbox
            checked={visibleKeys.includes(col.key)}
            onChange={(e) => {
              const checked = e.target.checked;
              setVisibleKeys((prev) =>
                checked
                  ? [...prev, col.key]
                  : prev.filter((k) => k !== col.key),
              );
            }}
          >
            {col.label}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  // BUILD TABLE COLUMNS WITH FILTERS & SORTERS
  const tableColumns = [
{
  title: "No.",

  width: 80,
     render: (_, __, index) => index + 1,
},

    ...ALL_COLUMNS.filter((col) => visibleKeys.includes(col.key)).map(
      ({ key, label }) => {
        const isNumeric = key === "opening_balance";

        const columnValues = [
          ...new Set(accountsList.map((a) => a[key])),
        ].filter(Boolean);

        return {
          title: label,
          dataIndex: key,
          align: isNumeric ? "right" : "left",
          render:
            key === "opening_balance"
              ? (val) => format(Number(val || 0).toFixed(2))
              : key === "created_date"
                ? (val) => (val ? formatDateOnly(val) : "-")
                : key === "acc_name"
                  ? (val, record) => (
                      <>
                        {val}
                        {record.is_system_account ? (
                          <Tag icon={<LockOutlined />} color="default" style={{ marginLeft: 8 }}>
                            System
                          </Tag>
                        ) : null}
                      </>
                    )
                  : (val) => val,

          // Sorting
          sorter:
            typeof accountsList[0]?.[key] === "string"
              ? (a, b) => (a[key] || "").localeCompare(b[key] || "")
              : (a, b) => (a[key] || 0) - (b[key] || 0),

          // Column Filters
          filters: !isNumeric
            ? columnValues.map((val) => ({
              text: String(val),
              value: val,
            }))
            : null,

          onFilter:
            !isNumeric && columnValues.length
              ? (value, record) => record[key] === value
              : undefined,
        };
      },
    ),

    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => {
            const isDefault = defaults.includes(record.acc_name?.trim());

            return (
              <>
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  disabled={!(isAdminUser || canEdit)}
                  onClick={() => {
                    dispatch(setSelectedAccount(record));
                    dispatch(setIsEditAccount(true));
                    navigate("addNewAccount");
                  }}
                />

                {!isDefault && !record.is_system_account && (
                  <Button
                    disabled={!(isAdminUser || canDelete)}
                    danger
                    type="link"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      handleDelete(record.acc_id, record.acc_name)
                    }
                  />
                )}
              </>
            );
          },
        },
      ]
      : []),
  ];

  return (
    <>
      {/* HEADER ROW */}
      <Row justify="space-between" align="middle" style={{ margin: "32px 0" }}>
        <Col style={{ marginBottom: "12px" }}>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 250 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>

        <Col style={{ display: "flex", gap: screens.md ? "1rem" : "0.5rem" }}>
         
          <Dropdown overlay={columnMenu} placement="bottomRight">
            <Button>Columns</Button>
          </Dropdown>

          <Button icon={<DownloadOutlined />} onClick={exportCSV}>
            {screens.md ? "Export" : ""}
          </Button>
        
          {/* <Button icon={<ReloadOutlined />} onClick={() => setSearchText("")}>
            {screens.md ? "Reset" : ""}
          </Button> */}

          {access_type === "WRITE" && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("addNewAccount")}
            >
              Add New
            </Button>
          )}
             <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 16 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
      </Row>

      {/* TABLE */}
      <Table
        columns={tableColumns}
        dataSource={treeAccounts}
        rowKey={(record, index) => record.acc_id || index}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Account Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={450}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Account Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Account Management module is used to create, organize, and manage
            financial ledger accounts within the ERP system. Accounts are used
            throughout sales, purchase, inventory, and accounting operations.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View and search account records</li>
              <li>Use <strong>Add New</strong> to create accounts</li>
              <li>Edit account details when required</li>
              <li>Delete non-default accounts if permitted</li>
              <li>Export account records to CSV</li>
              <li>Customize visible table columns</li>
              <li>Filter accounts using column filters</li>
              <li>Manage parent and child account structures</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Default accounts cannot be deleted.</li>
              <li>Account structure affects accounting reports and transactions.</li>
              <li>Parent-child hierarchy helps organize ledger accounts.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Exported reports reflect currently filtered data.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AccountList;
